import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { selectMyChatPermissions } from "../../selectors/chatSelectors";
import { Button, Form, Modal } from "react-bootstrap";
import { updateChatData, updateChatRole, transferChatOwnership, removeChatMember, leaveChat, setChatNotificationsMuted, loadChats } from "../../actions/chatActions";
import AddMembersModal from "./AddMembersModal";
import { selectIdentity } from "../../selectors/userSelectors";
import PresenceStatusText from "../../features/presence/ui/PresenceStatusText";
import { selectPresenceByUserId } from "../../features/presence/model/presenceSelectors";
import { CHAT_IMAGE_ACCEPT, CHAT_UNSUPPORTED_IMAGE_MESSAGE, isHeicLike } from "../../shared/imageFileContract";
import AvatarCropEditor from "../../shared/avatarCrop/AvatarCropEditor";
import { isChatLogoCropEligible } from "../../shared/avatarCrop/avatarCropEligibility";
import { SERVER_PREVIEW_CONFIRM_MESSAGE } from "../../shared/mediaPrepare/constants";
import {
  createPrepareSession,
  mapPrepareErrorMessage,
  prepareImageForEditor,
} from "../../shared/mediaPrepare/prepareImageForEditor";
import {
  blockUser as blockUserRequest,
  unblockUser as unblockUserRequest,
} from "../../screens/user/api/userBlocks";
import UserBlockConfirmModal from "../../screens/user/UserBlockConfirmModal";
import { CHAT_UPDATE_DATA_SUCCESS } from "../../constants/chatConstants";

const CHAT_MUTATION_FALLBACK = "Не вдалося виконати дію. Спробуйте ще раз.";

function muteStatusLabel(muted) {
  return muted ? "Сповіщення: вимкнено" : "Сповіщення: увімкнено";
}

function muteActionLabel(muted) {
  return muted ? "Увімкнути сповіщення" : "Вимкнути сповіщення";
}

function safeChatError(result, fallback = CHAT_MUTATION_FALLBACK) {
  const detail = result && result.error;
  if (typeof detail === "string" && detail.trim()) return detail;
  return fallback;
}
export default function ChatHeader({ chat }) {
  const identity = useSelector(selectIdentity);
  const activeChatId = useSelector(state => state.chat.activeChatId);
  const userId = identity?.userId ?? null;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const members = useSelector(state => state.chat.membersByChat[chat?.id] || []);
  const permissions = useSelector(state =>
    chat && identity
      ? selectMyChatPermissions(state, chat.id, identity.userId)
      : {}
  );

  const otherMember = members.find(m => String(m.user.id) !== String(userId)) || members[1];
  const otherUser = otherMember?.user;

  // Presence subscription ownership lives in ChatSidebar (always mounted by
  // ChatsLayout). Header only consumes Redux presence — no competing setDesired.
  const peerPresence = useSelector((state) =>
    otherUser?.id != null ? selectPresenceByUserId(state, otherUser.id) : null
  );
  const presenceByUserId = useSelector((state) => state.presence?.byUserId || {});

  const [removedChatId, setRemovedChatId] = useState(null);
  useEffect(() => {
    if (!removedChatId) return;
    if (activeChatId === removedChatId) {
      navigate("/chats");
    }
  }, [activeChatId, removedChatId, navigate]);

  const [confirmAction, setConfirmAction] = useState(null);
  const ACTIONS = {
    PROMOTE: "promote",
    DEMOTE: "demote",
    OWNER: "owner",
    REMOVE: "remove",
  }

  const [showChatSettingModal, setShowChatSettingModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [leaveIntent, setLeaveIntent] = useState(null);

  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [editPending, setEditPending] = useState(false);
  const [memberActionPending, setMemberActionPending] = useState(false);
  const [leavePending, setLeavePending] = useState(false);
  const [mutePending, setMutePending] = useState(false);
  const [muteError, setMuteError] = useState(null);
  const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
  const [blockPending, setBlockPending] = useState(false);
  const editPendingRef = useRef(false);
  const memberActionPendingRef = useRef(false);
  const leavePendingRef = useRef(false);
  const mutePendingRef = useRef(false);
  const blockPendingRef = useRef(false);

  const notificationsMuted = Boolean(chat?.my_notifications_muted);
  const blockedByMe = Boolean(chat?.blocked_by_me);

  const patchPrivateBlockState = (next) => {
    if (!chat?.id) return;
    dispatch({
      type: CHAT_UPDATE_DATA_SUCCESS,
      payload: {
        ...chat,
        blocked_by_me: Boolean(next.blocked_by_me),
        messaging_available: Boolean(next.messaging_available),
      },
    });
  };

  const openBlockConfirm = () => {
    if (blockPendingRef.current) return;
    setBlockConfirmOpen(true);
  };

  const closeBlockConfirm = () => {
    if (blockPendingRef.current) return;
    setBlockConfirmOpen(false);
  };

  const confirmBlock = async () => {
    if (!otherUser?.id || blockPendingRef.current) return;
    blockPendingRef.current = true;
    setBlockPending(true);
    try {
      await blockUserRequest({
        user_id: otherUser.id,
        public_id: otherUser.public_id,
      });
      patchPrivateBlockState({ blocked_by_me: true, messaging_available: false });
      setBlockConfirmOpen(false);
      toast.success("Користувача заблоковано.");
      dispatch(loadChats());
    } catch (_err) {
      toast.error("Не вдалося заблокувати користувача.");
    } finally {
      blockPendingRef.current = false;
      setBlockPending(false);
    }
  };

  const handleUnblock = async () => {
    if (!otherUser?.id || blockPendingRef.current) return;
    blockPendingRef.current = true;
    setBlockPending(true);
    try {
      await unblockUserRequest(otherUser.id);
      // Clear local blocked-by-me immediately; server list hydrate decides
      // whether messaging_available is restored (opposite-direction block).
      patchPrivateBlockState({
        blocked_by_me: false,
        messaging_available: false,
      });
      toast.success("Користувача розблоковано.");
      await dispatch(loadChats());
    } catch (_err) {
      toast.error("Не вдалося розблокувати користувача.");
    } finally {
      blockPendingRef.current = false;
      setBlockPending(false);
    }
  };

  const toggleChatNotifications = async () => {
    if (!chat?.id || mutePendingRef.current) return;
    mutePendingRef.current = true;
    setMutePending(true);
    setMuteError(null);
    const nextMuted = !notificationsMuted;
    const result = await dispatch(setChatNotificationsMuted(chat.id, nextMuted));
    mutePendingRef.current = false;
    setMutePending(false);
    if (!result?.ok) {
      setMuteError(safeChatError(result, "Не вдалося оновити сповіщення чату."));
      toast.error(safeChatError(result, "Не вдалося оновити сповіщення чату."));
    }
  };

  const [name, setName] = useState(chat?.name || "");
  const [file, setFile] = useState(null);
  const [pendingCropSource, setPendingCropSource] = useState(null);
  const [prepareStatus, setPrepareStatus] = useState(null);
  const [logoError, setLogoError] = useState(null);
  const editFileInputRef = useRef(null);
  const editChatIdRef = useRef(chat?.id ?? null);
  const prepareSessionRef = useRef(null);
  const activeRunIdRef = useRef(0);

  useEffect(
    () => () => {
      // Unmount: abort + invalidate so late prepare completions cannot commit.
      prepareSessionRef.current?.abort();
      prepareSessionRef.current = null;
      activeRunIdRef.current += 1;
    },
    [],
  );

  const clearEditImageState = () => {
    prepareSessionRef.current?.abort();
    prepareSessionRef.current = null;
    activeRunIdRef.current += 1;
    setFile(null);
    setPendingCropSource(null);
    setPrepareStatus(null);
    setLogoError(null);
    if (editFileInputRef.current) {
      editFileInputRef.current.value = "";
    }
  };

  const openEditModal = () => {
    // Defense-in-depth: private 1:1 chats have no identity edit surface.
    if (!chat?.is_group) return;
    setName(chat?.name || "");
    clearEditImageState();
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    clearEditImageState();
  };

  // chat.id change: close edit UI and clear pending image/crop (prevent A→B leak).
  useEffect(() => {
    const nextId = chat?.id ?? null;
    if (editChatIdRef.current !== nextId) {
      editChatIdRef.current = nextId;
      setShowEditModal(false);
      setName(chat?.name || "");
      clearEditImageState();
    }
  }, [chat?.id, chat?.name]);

  const cropActive = Boolean(pendingCropSource);
  const preparePending = Boolean(
    prepareStatus &&
      (prepareStatus.state === "native_check_pending" ||
        prepareStatus.state === "server_preview_pending" ||
        prepareStatus.state === "selected"),
  );
  const saveBlocked = cropActive || preparePending || !name.trim() || editPending;

  const startHeicPrepare = async (nextFile) => {
    prepareSessionRef.current?.abort();
    const session = createPrepareSession();
    prepareSessionRef.current = session;
    activeRunIdRef.current = session.runId;
    setLogoError(null);
    setPrepareStatus({ state: "selected", runId: session.runId });
    setPendingCropSource(null);
    setFile(null);

    const result = await prepareImageForEditor(nextFile, {
      surface: "chat_logo",
      runId: session.runId,
      signal: session.signal,
      confirmMessage: SERVER_PREVIEW_CONFIRM_MESSAGE,
      isCurrent: () => activeRunIdRef.current === session.runId,
      onStateChange: (next) => {
        if (activeRunIdRef.current !== session.runId) return;
        setPrepareStatus(next);
      },
    });

    if (activeRunIdRef.current !== session.runId) return;

    if (result.kind === "native" || result.kind === "server") {
      setPrepareStatus({ state: result.state, runId: session.runId });
      setPendingCropSource(result.file);
      setLogoError(null);
      return;
    }
    if (result.kind === "cancelled") {
      setPrepareStatus(null);
      setPendingCropSource(null);
      return;
    }
    setPrepareStatus(null);
    setPendingCropSource(null);
    setLogoError(mapPrepareErrorMessage(result.code, result.detail));
  };

  const handleEditImageChange = (event) => {
    const next = event.target.files && event.target.files[0];
    if (editFileInputRef.current) {
      editFileInputRef.current.value = "";
    }
    if (!next) return;
    if (isHeicLike(next)) {
      startHeicPrepare(next);
      return;
    }
    if (isChatLogoCropEligible(next)) {
      prepareSessionRef.current?.abort();
      activeRunIdRef.current += 1;
      setPrepareStatus(null);
      setLogoError(null);
      setPendingCropSource(next);
      return;
    }
    prepareSessionRef.current?.abort();
    activeRunIdRef.current += 1;
    setPendingCropSource(null);
    setPrepareStatus(null);
    setFile(null);
    setLogoError(CHAT_UNSUPPORTED_IMAGE_MESSAGE);
  };

  const handleCropApply = (croppedFile) => {
    setPendingCropSource(null);
    setPrepareStatus(null);
    setLogoError(null);
    setFile(croppedFile);
  };

  const handleCropCancel = () => {
    setPendingCropSource(null);
    setPrepareStatus(null);
  };

  const handleChange = async () => {
    if (saveBlocked || editPendingRef.current) return;
    const targetChatId = editChatIdRef.current ?? chat?.id;
    if (!targetChatId) return;
    editPendingRef.current = true;
    setEditPending(true);
    try {
      // No new image → omit image FormData key (existing logo unchanged).
      const result = await dispatch(updateChatData(targetChatId, name, file));
      if (result && result.ok) {
        toast.success("Чат оновлено.");
        closeEditModal();
      } else {
        toast.error(safeChatError(result, "Не вдалося оновити чат."));
      }
    } finally {
      editPendingRef.current = false;
      setEditPending(false);
    }
  };

  const handleLeaveChat = async () => {
    if (!leaveIntent || !permissions.canLeaveChat || leavePendingRef.current) return;
    leavePendingRef.current = true;
    setLeavePending(true);
    try {
      const result = await dispatch(leaveChat(leaveIntent.chatId));
      if (result && result.ok) {
        toast.success("Ви вийшли з чату.");
        setRemovedChatId(leaveIntent.chatId);
        setLeaveIntent(null);
      } else {
        toast.error(safeChatError(result, "Не вдалося вийти з чату."));
      }
    } finally {
      leavePendingRef.current = false;
      setLeavePending(false);
    }
  };

  const handleConfirmMemberAction = async () => {
    if (!confirmAction || memberActionPendingRef.current) return;
    const actionUserId = confirmAction?.userId;
    memberActionPendingRef.current = true;
    setMemberActionPending(true);
    try {
      let result = null;
      let successToast = null;
      switch (confirmAction.type) {
        case ACTIONS.OWNER:
          result = await dispatch(transferChatOwnership(chat.id, actionUserId));
          successToast = "Права власника передано.";
          break;
        case ACTIONS.PROMOTE:
          result = await dispatch(updateChatRole(chat.id, actionUserId, "admin"));
          successToast = "Права адміністратора надано.";
          break;
        case ACTIONS.DEMOTE:
          result = await dispatch(updateChatRole(chat.id, actionUserId, "member"));
          successToast = "Права адміністратора знято.";
          break;
        case ACTIONS.REMOVE:
          result = await dispatch(removeChatMember(chat.id, actionUserId));
          successToast = "Учасника видалено з чату.";
          break;
        default:
          break;
      }
      if (result && result.ok) {
        if (successToast) toast.success(successToast);
        setConfirmAction(null);
        setShowChatSettingModal(false);
      } else if (result) {
        toast.error(safeChatError(result));
      }
    } finally {
      memberActionPendingRef.current = false;
      setMemberActionPending(false);
    }
  };

  const ACTION_CONFIG = {
    [ACTIONS.PROMOTE]: {
      title: "Надати права адміністратора?",
      text: "Ви впевнені, що хочете надати права адміністратора",
    },
    [ACTIONS.DEMOTE]: {
      title: "Понизити до учасника?",
      text: "Ви впевнені, що хочете понизити до учасника",
    },
    [ACTIONS.OWNER]: {
      title: "Передати права власника?",
      text: "Ви впевнені, що хочете передати права власника",
    },
    [ACTIONS.REMOVE]: {
      title: "Видалити користувача?",
      text: "Ви впевнені, що хочете видалити з чату користувача",
    },
  };

  if (!chat) return null;
  if (!members.length) return null;
  return (
    <div className="chat-header">
      <Link to={'/chats/'} className="arrow-back d-flex d-lg-none" aria-label="Назад до списку чатів">
        <i className="fa-solid fa-arrow-left fa-2x"></i>
      </Link>
      {chat?.is_group ? (
        <>
          <AddMembersModal
            show={showAddMembersModal}
            onHide={() => setShowAddMembersModal(false)}
            chatId={chat.id}
          />
          <div className="user-link" onClick={() => setShowChatSettingModal(true)}>
            <span className="chat-logo-span">
              <img
                src={chat.image ? chat.image : "/images/no-profile-picture.png"}
                alt={chat.name ? chat.name : ""}
                className="content-user-photo" />
            </span>
            <span className="text">{chat.name ? chat.name : 'Група'}</span>
          </div>
          <Modal
            show={showChatSettingModal}
            onHide={() => setShowChatSettingModal(false)}
            aria-labelledby="contained-modal-title-vcenter"
            centered
            scrollable
            className="project-modal"
          >
            <Modal.Header>
              <Modal.Title className="ms-auto fs-2">{chat.name}</Modal.Title>
              <div className="modal-btn-close" onClick={() => setShowChatSettingModal(false)}>
                <i className="fa-solid fa-xmark fa-2x"></i>
              </div>
            </Modal.Header>
            <Modal.Body className="pt-0">
              {chat.image && (
                <img src={chat.image} className="img-fluid" alt={chat.name} />
              )}
              <div className="chat-buttons">
                {permissions.canEditChat && (
                  <div
                    className="chat-button"
                    onClick={() => { setShowChatSettingModal(false); openEditModal(); }}
                    data-action="edit-chat"
                  >
                    <div className="button-icon">
                      <i className="fa-solid fa-pen-to-square"></i>
                    </div>
                    <div className="button-text">
                      Редагувати
                    </div>
                  </div>
                )}
                {permissions.canManageMembers && (
                  <div className="chat-button"
                    onClick={() => {
                      setShowChatSettingModal(false);
                      setShowAddMembersModal(true);
                    }}>
                    <div className="button-icon">
                      <i className="fa-solid fa-user-plus"></i>
                    </div>
                    <div className="button-text">
                      Додати
                    </div>
                  </div>
                )}
                <div
                  className="chat-button"
                  onClick={() => {
                    if (mutePending) return;
                    void toggleChatNotifications();
                  }}
                  data-action="chat-notifications-toggle"
                  aria-disabled={mutePending}
                  aria-label={muteActionLabel(notificationsMuted)}
                  title={muteActionLabel(notificationsMuted)}
                >
                  <div className="button-icon">
                    <i className={notificationsMuted ? "fa-solid fa-bell-slash" : "fa-solid fa-bell"}></i>
                  </div>
                  <div className="button-text">
                    {muteStatusLabel(notificationsMuted)}
                  </div>
                </div>
                {muteError ? (
                  <div className="text-danger small mt-2" data-chat-notifications-error="true">
                    {muteError}
                  </div>
                ) : null}
                <div
                  className="chat-button chat-button--danger"
                  onClick={() => {
                    setShowChatSettingModal(false);
                    setLeaveIntent({ chatId: chat.id, chatName: chat.name });
                  }}
                >
                  <div className="button-icon">
                    <i className="fa-solid fa-right-from-bracket"></i>
                  </div>
                  <div className="button-text">
                    Вийти
                  </div>
                </div>
              </div>
              <div className="people">
                <div className="text-center fs-3">{members.length} Учасники</div>
                {members.map((member) => (
                  <div className="row chat-user" key={member.user.id}>
                    <div className="col-2 user-avatar">
                      <div className="avatar">
                        <img
                          src={member.user.user_image || "/images/no-profile-picture.png"}
                          alt={member.user.fullname}
                        />
                      </div>
                    </div>
                    <div className="col-6 user-info">
                      <div className="user-chat-position">
                        {member.role === "owner" ? 'Власник' : member.role === "admin" ? 'Адміністратор' : 'Учасник'}
                      </div>
                      <Link
                        to={
                          member.user.handle || member.user.public_id
                            ? `/user/${member.user.handle || member.user.public_id}`
                            : "#"
                        }
                        className="user-name"
                      >
                        {member.user.fullname}
                      </Link>
                      <div className="user-last-active">
                        <PresenceStatusText
                          className="chat-member-presence"
                          presence={
                            member.user?.id != null
                              ? presenceByUserId[String(member.user.id)] || null
                              : null
                          }
                        />
                      </div>
                    </div>
                    {(member.user.id !== userId && permissions.canManageMembers) && (
                      <div className="col-4 user-button">
                        {((permissions.isOwner && (member.role === "admin" || member.role === "member")) || (permissions.isAdmin && member.role === "member")) && (
                          <button
                            type="button"
                            className="chat-member-action"
                            aria-label={
                              member.role === "admin"
                                ? `Передати права власника: ${member.user.fullname}`
                                : `Надати права адміністратора: ${member.user.fullname}`
                            }
                            disabled={memberActionPending}
                            onClick={() =>
                              setConfirmAction({
                                type:
                                  member.role === "admin"
                                    ? ACTIONS.OWNER
                                    : ACTIONS.PROMOTE,
                                userId: member.user.id,
                                fullname: member.user.fullname,
                              })
                            }
                          >
                            <i className="fa-solid fa-arrow-up" aria-hidden="true" />
                          </button>
                        )}
                        {(permissions.isOwner && member.role === "admin") && (
                          <button
                            type="button"
                            className="chat-member-action"
                            aria-label={`Понизити до учасника: ${member.user.fullname}`}
                            disabled={memberActionPending}
                            onClick={() =>
                              setConfirmAction({
                                type: ACTIONS.DEMOTE,
                                userId: member.user.id,
                                fullname: member.user.fullname,
                              })
                            }
                          >
                            <i className="fa-solid fa-arrow-down" aria-hidden="true" />
                          </button>
                        )}
                        {((permissions.isOwner && (member.role === "admin" || member.role === "member")) || (permissions.isAdmin && member.role === "member")) && (
                          <button
                            type="button"
                            className="chat-member-action chat-member-action--danger"
                            aria-label={`Видалити з чату: ${member.user.fullname}`}
                            disabled={memberActionPending}
                            onClick={() =>
                              setConfirmAction({
                                type: ACTIONS.REMOVE,
                                userId: member.user.id,
                                fullname: member.user.fullname,
                              })
                            }
                          >
                            <i className="fa-solid fa-sign-out" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Modal.Body>
          </Modal>
          <Modal
            show={!!confirmAction}
            onHide={() => {
              if (memberActionPending) return;
              setConfirmAction(null);
            }}
            className="project-modal"
            centered
          >
            <Modal.Header>
              <Modal.Title>
                {ACTION_CONFIG[confirmAction?.type]?.title}
              </Modal.Title>
              <div
                className="modal-btn-close"
                onClick={() => {
                  if (memberActionPending) return;
                  setConfirmAction(null);
                }}
              >
                <i className="fa-solid fa-xmark fa-2x"></i>
              </div>
            </Modal.Header>

            <Modal.Body>
              {confirmAction?.userId && (
                <p>
                  {ACTION_CONFIG[confirmAction?.type]?.text}{" "}
                  <b>{confirmAction?.fullname}</b>?
                </p>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setConfirmAction(null)}
                disabled={memberActionPending}
              >
                Скасувати
              </Button>

              <Button
                variant={confirmAction?.type === ACTIONS.REMOVE ? "danger" : "primary"}
                onClick={handleConfirmMemberAction}
                disabled={memberActionPending}
              >
                Так
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      ) : (
        <Link
          to={
            otherUser?.handle || otherUser?.public_id
              ? `/user/${otherUser.handle || otherUser.public_id}`
              : "#"
          }
          className="user-link"
        >
          <span className="chat-logo-span">
            <img
              src={otherUser?.user_image ? otherUser.user_image : "/images/no-profile-picture.png"}
              alt={otherUser?.fullname || ""}
              className="content-user-photo"
            />
          </span>
          <span className="text chat-peer-identity">
            <span className="chat-peer-name">{otherUser?.fullname || ""}</span>
            <PresenceStatusText
              presence={peerPresence}
              className="chat-peer-presence"
            />
          </span>
        </Link>
      )}
      {!chat?.is_group && (
        <div className="dropdown">
          <div className="chat-setting-btn" data-bs-toggle="dropdown" data-bs-auto-close="outside"
            aria-expanded="false">
            <i className="fa-solid fa-gear fa-2x"></i>
          </div>
          <ul className="dropdown-menu">
            {/* Private 1:1 never exposes identity edit — group-only
                (NOTIFICATION-COMPLETION-LIVE-QA-POLISH-B1 / #339). */}
            <li>
              <div
                className="dropdown-item"
                onClick={() => {
                  if (mutePending) return;
                  void toggleChatNotifications();
                }}
                data-action="chat-notifications-toggle"
                aria-disabled={mutePending}
                aria-label={muteActionLabel(notificationsMuted)}
                title={muteActionLabel(notificationsMuted)}
              >
                <i className={`me-2 ${notificationsMuted ? "fa-solid fa-bell-slash" : "fa-solid fa-bell"}`}></i>
                {muteStatusLabel(notificationsMuted)}
              </div>
            </li>
            {muteError ? (
              <li>
                <div className="dropdown-item text-danger small" data-chat-notifications-error="true">
                  {muteError}
                </div>
              </li>
            ) : null}
            {otherUser?.id ? (
              <li>
                {blockedByMe ? (
                  <div
                    className="dropdown-item"
                    onClick={() => {
                      if (blockPending) return;
                      void handleUnblock();
                    }}
                    data-action="chat-unblock-user"
                    aria-disabled={blockPending}
                  >
                    <i className="fa-solid fa-lock-open me-2" aria-hidden="true"></i>
                    Розблокувати користувача
                  </div>
                ) : (
                  <div
                    className="dropdown-item text-danger"
                    onClick={openBlockConfirm}
                    data-action="chat-block-user"
                    aria-disabled={blockPending}
                  >
                    <i className="fa-solid fa-user-slash me-2" aria-hidden="true"></i>
                    Заблокувати користувача
                  </div>
                )}
              </li>
            ) : null}
          </ul>
        </div>
      )}

      {/* Authoritative chat identity edit modal — group chats only. */}
      <Modal
        show={Boolean(showEditModal && chat?.is_group)}
        onHide={closeEditModal}
        aria-labelledby="chat-edit-modal-title"
        centered
        className="project-modal"
        data-chat-edit-modal="true"
      >
        <Modal.Header closeButton>
          <Modal.Title id="chat-edit-modal-title">Редагувати чат</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="my-3" controlId="chatEditName">
            <Form.Label>Назва чату</Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="Введіть назву"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-field="chat-edit-name"
            />
          </Form.Group>

          {chat?.image && !file && !cropActive && (
            <div className="mb-3" data-chat-edit-current-image="true">
              <img
                src={chat.image}
                alt=""
                className="img-fluid"
                style={{ maxWidth: 96, maxHeight: 96 }}
              />
            </div>
          )}

          <Form.Group controlId="chatEditFile" className="mb-3">
            <Form.Label>Фотографія</Form.Label>
            <Form.Control
              type="file"
              accept={CHAT_IMAGE_ACCEPT}
              ref={editFileInputRef}
              onChange={handleEditImageChange}
              data-field="chat-edit-image"
            />
            {preparePending && (
              <Form.Text className="d-block mt-2" role="status" data-prepare-state={prepareStatus?.state}>
                {prepareStatus?.state === "server_preview_pending"
                  ? "Підготовка попереднього перегляду на сервері…"
                  : "Перевірка зображення…"}
              </Form.Text>
            )}
            {logoError && (
              <Form.Text className="d-block mt-2 text-danger" role="alert">
                {logoError}
              </Form.Text>
            )}
            {file && !cropActive && !preparePending && (
              <Form.Text className="d-block mt-2" role="status">
                Обрано: {file.name}
              </Form.Text>
            )}
          </Form.Group>

          {cropActive && (
            <AvatarCropEditor
              sourceFile={pendingCropSource}
              title="Обрізати фото чату"
              onApply={handleCropApply}
              onCancel={handleCropCancel}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEditModal}>
            Закрити
          </Button>
          <Button
            variant="primary"
            onClick={handleChange}
            disabled={saveBlocked}
            data-action="chat-edit-save"
          >
            {editPending ? "Збереження…" : "Зберегти зміни"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!leaveIntent}
        onHide={() => {
          if (leavePending) return;
          setLeaveIntent(null);
        }}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="project-modal"
      >
        <Modal.Header closeButton={!leavePending}>
          <Modal.Title>Вийти з чату</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {permissions.canLeaveChat ? (
            <p>Ви хочете вийти з {leaveIntent?.chatName ? `чату "${leaveIntent.chatName}"` : "цього чату"}?</p>
          ) : (
            <p>На даний момент ви не можете вийти з цього чату. Спочатку передайте права "Власника" чату іншому
              користувачу.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setLeaveIntent(null)}
            disabled={leavePending}
          >
            Закрити
          </Button>
          {permissions.canLeaveChat && (
            <Button
              variant="danger"
              onClick={handleLeaveChat}
              disabled={leavePending}
            >
              {leavePending ? "Вихід…" : "Так"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <UserBlockConfirmModal
        open={blockConfirmOpen}
        userName={otherUser?.fullname || ""}
        pending={blockPending}
        onCancel={closeBlockConfirm}
        onConfirm={confirmBlock}
        context="private_chat"
      />
    </div>
  );
}
